import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import AreaForm from "../../components/ui/AreaForm";
import { toast } from "react-toastify";
import areasIniciales from "../../utils/seed/areas";
import { LoaderPinwheel } from "lucide-react";
import { hasPermission } from "../../utils/permissions";
import PageSizeSelect from "../../components/ui/PageSizeSelect";

const AreasPage = () => {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredAreas, setFilteredAreas] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [importando, setImportando] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(6);

  const token = localStorage.getItem("token");
  const API_URL = process.env.REACT_APP_API_URL + "/areas";

  const fetchAreas = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAreas(res.data);
      setFilteredAreas(res.data);
    } catch (err) {
      toast.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAreas();
    // eslint-disable-next-line
  }, []);

  // 🔍 Filtrado de búsqueda
  useEffect(() => {
    const filtered = areas.filter(
      (a) =>
        a.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (a.grupo?.name || "").toLowerCase().includes(searchTerm.toLowerCase()),
    );
    setFilteredAreas(filtered);
    setCurrentPage(1); // Reinicia a la primera página al filtrar
  }, [searchTerm, areas]);

  const handleEdit = (area) => {
    setSelectedArea(area);
    setShowModal(true);
  };

  const handleCreate = () => {
    setSelectedArea(null);
    setShowModal(true);
  };

  const handleSave = async (formData) => {
    try {
      if (selectedArea) {
        await axios.put(`${API_URL}/${selectedArea._id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Área actualizada correctamente ✅");
      } else {
        await axios.post(API_URL, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Área creada correctamente ✅");
      }
      setShowModal(false);
      fetchAreas();
    } catch (err) {
      const backendMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Ocurrió un error inesperado al guardar el área";
      toast.error(backendMessage);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta área?")) return;
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchAreas();
    } catch (err) {
      toast.error("Error al eliminar área:", err);
    }
  };

  const handleImportarBase = async () => {
    if (!window.confirm("¿Deseas importar todas las áreas iniciales?")) return;

    try {
      setImportando(true);
      await axios.post(
        `${API_URL}/importar`,
        { areas: areasIniciales },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Áreas importadas correctamente ✅");
      fetchAreas(); // recarga la lista
    } catch (err) {
      toast.error("Ocurrió un error al importar las áreas");
    } finally {
      setImportando(false);
    }
  };

  // 📄 Paginación
  const totalPages = Math.ceil(filteredAreas.length / itemsPerPage);
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = filteredAreas.slice(indexOfFirst, indexOfLast);

  return (
    <Card className="shadow-md">
      <CardContent>
        <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
          <h2 className="text-lg font-semibold">Áreas</h2>
          <div className="flex gap-2 items-center w-full sm:w-auto">
            <input
              type="text"
              placeholder="Buscar categoría..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            {hasPermission("areas.crear") && (
              <Button onClick={handleCreate}>+ Nueva Área</Button>
            )}
          </div>
        </div>

        {/* 🔹 Botón importar si no hay áreas */}
        {!loading && areas.length === 0 && (
          <div className="my-4 text-center">
            <Button onClick={handleImportarBase} disabled={importando}>
              {importando ? "Importando..." : "Importar base de áreas"}
            </Button>
          </div>
        )}

        {loading && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
            <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
            Cargando datos, por favor espera...
          </div>
        )}

        {/* 🧾 Tabla */}
        <div className="flex items-center justify-end mb-2">
          <PageSizeSelect
            value={itemsPerPage}
            onChange={(size) => {
              setItemsPerPage(size);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-lg shadow-sm">
            <thead className="bg-gray-100 text-gray-700 text-sm uppercase">
              <tr>
                <th className="py-2 px-4 text-left">Nombre</th>
                <th className="py-2 px-4 text-left">Grupo</th>
                <th className="py-2 px-4 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.length > 0 ? (
                currentItems.map((c) => (
                  <tr key={c._id} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-4">{c.name}</td>
                    <td className="py-2 px-4">{c.grupo?.name || "-"}</td>
                    <td className="py-2 px-4 text-right">
                      {hasPermission("areas.editar") && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(c)}
                        >
                          Editar
                        </Button>
                      )}
                      {hasPermission("areas.eliminar") && (
                        <Button
                          variant="destructive"
                          size="sm"
                          className="ml-2 bg-red-500 hover:bg-red-600 text-white"
                          onClick={() => handleDelete(c._id)}
                        >
                          Eliminar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="3"
                    className="text-center py-4 text-gray-500 italic"
                  >
                    No se encontraron resultados
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 🔢 Controles de paginación */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-between items-center mt-4 text-sm text-gray-600 gap-3">
            <span>
              Mostrando {currentItems.length} de {filteredAreas.length}{" "}
              resultados
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
              >
                ← Anterior
              </Button>
              <span className="px-2 py-1 bg-gray-100 rounded-md">
                Página {currentPage} de {totalPages || 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage((prev) => prev + 1)}
              >
                Siguiente →
              </Button>
            </div>
          </div>
        )}

        {showModal && (
          <AreaForm
            area={selectedArea}
            onClose={() => setShowModal(false)}
            onSave={handleSave}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default AreasPage;
